<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { Device, Form } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Device
}

const props = defineProps<Props>()
const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () => ({
    manufacturer: { required: requiredValidator },
    model: { required: requiredValidator },
    type: { required: requiredValidator },
    serialNumber: { required: requiredValidator },
    saleDate: { required: requiredValidator },
    salePrice: { required: requiredValidator },
    description: { required: requiredValidator },
    warrantyTime: { required: requiredValidator },
  }),
  model,
  { $scope: Form.device, $registerAs: Form.device }
)

const warrantyTimes = computed(() =>
  [3, 6, 9, 12, 18, 24].map((x) => ({ value: x, label: `${x} meses` }))
)
</script>

<template>
  <FormsGlobalContainer :identifier="Form.device">
    <FormsGlobalTitle
      text="Información del dispositivo"
      icon-class="icon-[ph--shopping-bag-open-bold]"
    />

    <InputField
      label="Marca"
      :error="v$.manufacturer?.$error"
      :error-message="v$.manufacturer?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.manufacturer" />
    </InputField>

    <InputField
      label="Modelo"
      :error="v$.model?.$error"
      :error-message="v$.model?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.model" />
    </InputField>

    <InputField
      label="Tipo"
      :error="v$.type?.$error"
      :error-message="v$.type?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.type"
        option-label="label"
        option-value="code"
        :options="Device.getDeviceTypeList()"
      />
    </InputField>

    <InputField
      label="Número serial o IMEI"
      :error="v$.serialNumber?.$error"
      :error-message="v$.serialNumber?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.serialNumber" />
    </InputField>

    <InputField
      label="Fecha de compra"
      :error="v$.saleDate?.$error"
      :error-message="v$.saleDate?.$errors[0]?.$message.toString()"
    >
      <PvDatePicker
        v-model="model.saleDate"
        :max-date="new Date()"
      />
    </InputField>

    <InputField
      label="Precio de compra (RD$)"
      :error="v$.salePrice?.$error"
      :error-message="v$.salePrice?.$errors[0]?.$message.toString()"
    >
      <PvInputNumber
        v-model="model.salePrice"
        :max-fraction-digits="2"
      />
    </InputField>

    <InputField
      label="Tiempo de garantía"
      :error="v$.warrantyTime?.$error"
      :error-message="v$.warrantyTime?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.warrantyTime"
        option-label="label"
        option-value="value"
        :options="warrantyTimes"
      />
    </InputField>

    <InputField
      label="Descripción"
      class="col-span-full"
      :error="v$.description?.$error"
      :error-message="v$.description?.$errors[0]?.$message.toString()"
    >
      <PvTextarea v-model="model.description" />
    </InputField>
  </FormsGlobalContainer>
</template>
